/* eslint-disable max-lines -- not split yet: screen-reader announcements, responsive/animation settings and option flatten/filter can become hooks */
import { cn, NoddiIcon } from 'noddi-ui-common';
import * as React from 'react';
import { Button } from '../../../base/button/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../base/command';
import {
  isMultiSelectGroupedOptions,
  type MultiSelectGroup,
  type MultiSelectOption
} from '../../../base/multi-select-utils';
import { mergeAriaIds } from '../../../helpers/fieldAccessibility';
import { NoddiSkeleton } from '../../Feedback/NoddiSkeleton';
import { NoddiSpinner } from '../../Feedback/NoddiSpinner';
import { NoddiBadge } from '../../Other/NoddiBadge';
import { NoddiSeparator } from '../../Other/NoddiSeparator';
import { Popover, PopoverContent, PopoverTrigger } from '../../Overlay/NoddiPopover';

/**
 * Animation types and configurations
 */
interface AnimationConfig {
  /** Popover animation type */
  popoverAnimation?: 'scale' | 'slide' | 'fade' | 'flip' | 'none';
  /** Option hover animation type */
  optionHoverAnimation?: 'highlight' | 'scale' | 'glow' | 'none';
  /** Animation duration in seconds */
  duration?: number;
  /** Animation delay in seconds */
  delay?: number;
}

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'animationConfig' | 'defaultValue'
> {
  loading?: boolean;
  /**
   * An array of option objects or groups to be displayed in the multi-select component.
   */
  options: MultiSelectOption[] | MultiSelectGroup[];
  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: Array<string | number>) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: Array<string | number>;

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Advanced animation configuration for different component parts.
   * Optional, allows fine-tuning of various animation effects.
   */
  animationConfig?: AnimationConfig;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * If true, disables the select all functionality.
   * Optional, defaults to false.
   */
  hideSelectAll?: boolean;

  /**
   * If true, shows search functionality in the popover.
   * If false, hides the search input completely.
   * Optional, defaults to true.
   */
  searchable?: boolean;

  /**
   * Custom empty state message when no options match search.
   * Optional, defaults to "No options found"
   */
  emptyIndicator?: React.ReactNode;

  /**
   * If true, allows the component to grow and shrink with its content.
   * If false, uses fixed width behavior.
   * Optional, defaults to false.
   */
  autoSize?: boolean;

  /**
   * If true, shows badges in a single line with horizontal scroll.
   * If false, badges wrap to multiple lines.
   * Optional, defaults to false.
   */
  singleLine?: boolean;

  /**
   * Custom CSS class for the popover content.
   * Optional, can be used to customize popover appearance.
   */
  popoverClassName?: string;

  /**
   * If true, disables the component completely.
   * Optional, defaults to false.
   */
  disabled?: boolean;

  /**
   * Responsive configuration for different screen sizes.
   * Allows customizing maxCount and other properties based on viewport.
   * Can be boolean true for default responsive behavior or an object for custom configuration.
   */
  responsive?:
    | boolean
    | {
        /** Configuration for mobile devices (< 640px) */
        mobile?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
        /** Configuration for tablet devices (640px - 1024px) */
        tablet?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
        /** Configuration for desktop devices (> 1024px) */
        desktop?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
      };

  /**
   * If true, automatically removes duplicate options based on their value.
   * Optional, defaults to false (shows warning in dev mode instead).
   */
  deduplicateOptions?: boolean;

  /**
   * If true, the component will reset its internal state when defaultValue changes.
   * Useful for React Hook Form integration and form reset functionality.
   * Optional, defaults to true.
   */
  resetOnDefaultValueChange?: boolean;

  /**
   * If true, automatically closes the popover after selecting an option.
   * Useful for single-selection-like behavior or mobile UX.
   * Optional, defaults to false.
   */
  closeOnSelect?: boolean;
  /**
   * Callback fired whenever internal search input value changes (before filtering debounce).
   * Useful for async option loading.
   */
  onSearchValueChange?: (value: string) => void;
  /** If true, internal search input is cleared when popover closes (default false). */
  clearSearchOnClose?: boolean;

  /** Custom row renderer for dropdown options (defaults to label text). */
  renderOption?: (option: MultiSelectOption) => React.ReactNode;
}

/**
 * Imperative methods exposed through ref
 */
export interface MultiSelectRef {
  /**
   * Programmatically reset the component to its default value
   */
  reset: () => void;
  /**
   * Get current selected values
   */
  getSelectedValues: () => Array<string | number>;
  /**
   * Set selected values programmatically
   */
  setSelectedValues: (values: Array<string | number>) => void;
  /**
   * Clear all selected values
   */
  clear: () => void;
  /**
   * Focus the component
   */
  focus: () => void;
}

export const MultiSelect = React.forwardRef<MultiSelectRef, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      defaultValue = [],
      placeholder = 'Select options',
      animation = 0,
      animationConfig,
      maxCount = 3,
      className,
      hideSelectAll = false,
      searchable = true,
      emptyIndicator,
      autoSize = false,
      singleLine = false,
      popoverClassName,
      disabled = false,
      responsive,
      deduplicateOptions = false,
      resetOnDefaultValueChange = true,
      closeOnSelect = false,
      onSearchValueChange,
      clearSearchOnClose = false,
      renderOption,
      loading,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<Array<string | number>>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');

    const [politeMessage, setPoliteMessage] = React.useState('');
    const [assertiveMessage, setAssertiveMessage] = React.useState('');
    const prevSelectedCount = React.useRef(selectedValues.length);
    const prevIsOpen = React.useRef(isPopoverOpen);
    const prevSearchValue = React.useRef(searchValue);

    const announce = React.useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
      if (priority === 'assertive') {
        setAssertiveMessage(message);
        setTimeout(() => setAssertiveMessage(''), 100);
      } else {
        setPoliteMessage(message);
        setTimeout(() => setPoliteMessage(''), 100);
      }
    }, []);

    const generatedMultiSelectId = React.useId();
    // Derived ids follow a caller-supplied id so a wrapper's <label htmlFor> lands on the trigger.
    const multiSelectId = props.id ?? generatedMultiSelectId;
    const triggerDescriptionId = `${multiSelectId}-description`;
    const selectionSummaryId = `${multiSelectId}-selection`;

    const prevDefaultValueRef = React.useRef<Array<string | number>>(defaultValue);

    const isOptionsGrouped = React.useMemo(() => isMultiSelectGroupedOptions(options), [options]);

    const arraysEqual = React.useCallback((a: Array<string | number>, b: Array<string | number>): boolean => {
      if (a.length !== b.length) {
        return false;
      }
      const sortedA = [...a].map(String).sort();
      const sortedB = [...b].map(String).sort();
      return sortedA.every((val, index) => val === sortedB[index]);
    }, []);

    const resetToDefault = React.useCallback(() => {
      setSelectedValues(defaultValue);
      setIsPopoverOpen(false);
      setSearchValue('');
      onValueChange(defaultValue);
    }, [defaultValue, onValueChange]);

    const buttonRef = React.useRef<HTMLButtonElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        reset: resetToDefault,
        getSelectedValues: () => selectedValues,
        setSelectedValues: (values: Array<string | number>) => {
          setSelectedValues(values);
          onValueChange(values);
        },
        clear: () => {
          setSelectedValues([]);
          onValueChange([]);
        },
        focus: () => {
          if (buttonRef.current) {
            buttonRef.current.focus();
            const originalOutline = buttonRef.current.style.outline;
            const originalOutlineOffset = buttonRef.current.style.outlineOffset;
            buttonRef.current.style.outline = '2px solid var(--ring)';
            buttonRef.current.style.outlineOffset = '2px';
            setTimeout(() => {
              if (buttonRef.current) {
                buttonRef.current.style.outline = originalOutline;
                buttonRef.current.style.outlineOffset = originalOutlineOffset;
              }
            }, 1000);
          }
        }
      }),
      [resetToDefault, selectedValues, onValueChange]
    );

    const [screenSize, setScreenSize] = React.useState<'mobile' | 'tablet' | 'desktop'>('desktop');

    React.useEffect(() => {
      if (typeof window === 'undefined') {
        return;
      }
      const handleResize = () => {
        const width = window.innerWidth;
        if (width < 640) {
          setScreenSize('mobile');
        } else if (width < 1024) {
          setScreenSize('tablet');
        } else {
          setScreenSize('desktop');
        }
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('resize', handleResize);
        }
      };
    }, []);

    const getResponsiveSettings = () => {
      if (!responsive) {
        return {
          maxCount,
          hideIcons: false,
          compactMode: false
        };
      }
      if (responsive === true) {
        const defaultResponsive = {
          mobile: { maxCount: 2, hideIcons: false, compactMode: true },
          tablet: { maxCount: 4, hideIcons: false, compactMode: false },
          desktop: { maxCount: 6, hideIcons: false, compactMode: false }
        };
        const currentSettings = defaultResponsive[screenSize];
        return {
          maxCount: currentSettings?.maxCount ?? maxCount,
          hideIcons: currentSettings?.hideIcons ?? false,
          compactMode: currentSettings?.compactMode ?? false
        };
      }
      const currentSettings = responsive[screenSize];
      return {
        maxCount: currentSettings?.maxCount ?? maxCount,
        hideIcons: currentSettings?.hideIcons ?? false,
        compactMode: currentSettings?.compactMode ?? false
      };
    };

    const responsiveSettings = getResponsiveSettings();

    const getPopoverAnimationClass = () => {
      if (animationConfig?.popoverAnimation) {
        switch (animationConfig.popoverAnimation) {
          case 'scale':
            return 'animate-in zoom-in-95';
          case 'slide':
            return 'animate-in slide-in-from-top-2';
          case 'fade':
            return 'animate-in fade-in-0';
          case 'flip':
            return 'animate-in zoom-in-95 duration-300';
          case 'none':
            return '';
          default:
            return '';
        }
      }
      return '';
    };

    const allOptionsWithDisabled = React.useMemo((): MultiSelectOption[] => {
      if (options.length === 0) {
        return [];
      }
      // eslint-disable-next-line no-restricted-syntax -- assigned by the grouped vs flat options branch
      let allOptions: MultiSelectOption[];
      if (isOptionsGrouped) {
        allOptions = (options as MultiSelectGroup[]).flatMap((group) => group.options);
      } else {
        allOptions = options as MultiSelectOption[];
      }
      const valueSet = new Set<string>();
      const duplicates: string[] = [];
      const uniqueOptions: MultiSelectOption[] = [];
      allOptions.forEach((option) => {
        const key = String(option.value);
        if (valueSet.has(key)) {
          duplicates.push(key);
          if (!deduplicateOptions) {
            uniqueOptions.push(option);
          }
        } else {
          valueSet.add(key);
          uniqueOptions.push(option);
        }
      });
      if (import.meta.env.DEV && duplicates.length > 0) {
        const action = deduplicateOptions ? 'automatically removed' : 'detected';
        console.warn(
          `MultiSelect: Duplicate option values ${action}: ${duplicates.join(', ')}. ` +
            `${
              deduplicateOptions
                ? 'Duplicates have been removed automatically.'
                : "This may cause unexpected behavior. Consider setting 'deduplicateOptions={true}' or ensure all option values are unique."
            }`
        );
      }
      return deduplicateOptions ? uniqueOptions : allOptions;
    }, [options, deduplicateOptions, isOptionsGrouped]);

    const getOptionByValue = React.useCallback(
      (value: string | number): MultiSelectOption | undefined => {
        const option = allOptionsWithDisabled.find((option) => option.value === value);
        if (!option && import.meta.env.DEV) {
          console.warn(`MultiSelect: Option with value "${value}" not found in options list`);
        }
        return option;
      },
      [allOptionsWithDisabled]
    );

    const selectedLabels = selectedValues
      .map((value) => allOptionsWithDisabled.find((option) => option.value === value)?.label)
      .filter(Boolean);

    const enabledOptions = React.useMemo(
      () => allOptionsWithDisabled.filter((option) => !option.disabled),
      [allOptionsWithDisabled]
    );
    const areAllSelected =
      enabledOptions.length > 0 && enabledOptions.every((option) => selectedValues.includes(option.value));

    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchValue) {
        return options;
      }
      if (options.length === 0) {
        return [];
      }
      if (isOptionsGrouped) {
        return (options as MultiSelectGroup[])
          .map((group) => ({
            ...group,
            options: group.options.filter(
              (option: MultiSelectOption) =>
                option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
                String(option.value).toLowerCase().includes(searchValue.toLowerCase())
            )
          }))
          .filter((group) => group.options.length > 0);
      }
      return (options as MultiSelectOption[]).filter(
        (option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
          String(option.value).toLowerCase().includes(searchValue.toLowerCase())
      );
    }, [options, searchValue, searchable, isOptionsGrouped]);

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (optionValue: string | number) => {
      if (disabled) {
        return;
      }
      const option = getOptionByValue(optionValue);
      if (option?.disabled) {
        return;
      }
      const newSelectedValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((value) => value !== optionValue)
        : [...selectedValues, optionValue];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    const handleClear = () => {
      if (disabled) {
        return;
      }
      setSelectedValues([]);
      onValueChange([]);
    };

    const toggleAll = () => {
      if (disabled) {
        return;
      }
      // Merge rather than replace, so options that are selected but disabled survive both directions.
      const enabledValues = enabledOptions.map((option) => option.value);
      const newSelectedValues = areAllSelected
        ? selectedValues.filter((value) => !enabledValues.includes(value))
        : Array.from(new Set([...selectedValues, ...enabledValues]));

      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);

      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    React.useEffect(() => {
      if (!resetOnDefaultValueChange) {
        return;
      }
      const prevDefaultValue = prevDefaultValueRef.current;
      if (!arraysEqual(prevDefaultValue, defaultValue)) {
        if (!arraysEqual(selectedValues, defaultValue)) {
          setSelectedValues(defaultValue);
        }
        prevDefaultValueRef.current = [...defaultValue];
      }
    }, [defaultValue, selectedValues, arraysEqual, resetOnDefaultValueChange]);

    React.useEffect(() => {
      if (!isPopoverOpen && clearSearchOnClose) {
        setSearchValue('');
        onSearchValueChange?.('');
      }
    }, [isPopoverOpen, clearSearchOnClose, onSearchValueChange]);

    // cmdk owns aria-selected on its items and uses it for the keyboard-highlighted row, not for
    // what the user has chosen — anything we pass is overwritten. The chosen state therefore has
    // to travel in the accessible name, which is the only signal assistive tech can trust here.
    const renderOptionItem = (option: MultiSelectOption) => {
      const isSelected = selectedValues.includes(option.value);
      return (
        <CommandItem
          key={String(option.value)}
          onSelect={() => toggleOption(option.value)}
          // oxlint-disable-next-line jsx-a11y/role-has-required-aria-props -- cmdk overwrites aria-selected; see the comment above
          role='option'
          aria-disabled={option.disabled}
          aria-label={`${option.label}${isSelected ? ', selected' : ', not selected'}${
            option.disabled ? ', disabled' : ''
          }`}
          className={cn('cursor-pointer', option.disabled && 'cursor-not-allowed opacity-50')}
          disabled={option.disabled}
          value={`${option.label} ${String(option.value)}`}
        >
          <div
            className={cn(
              'mr-2 flex size-4 items-center justify-center rounded-xs border border-primary',
              isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'
            )}
            aria-hidden='true'
          >
            <NoddiIcon name='Check' size='small' />
          </div>
          {renderOption ? <div className='min-w-0 flex-1'>{renderOption(option)}</div> : <span>{option.label}</span>}
          {option.imageUrl ? (
            <img
              src={option.imageUrl}
              alt={option.label}
              className='ml-2 h-4 w-auto object-contain'
              aria-hidden='true'
            />
          ) : option.icon ? (
            <NoddiIcon name={option.icon} className='ml-2 text-muted-foreground' aria-hidden='true' />
          ) : null}
        </CommandItem>
      );
    };

    // Create a stable key for the options to force re-render of the items list when options change
    // Note: We use this key on the CommandList (not the Command root) to avoid remounting the input and losing focus.
    const optionsKey = React.useMemo(() => {
      return JSON.stringify(options);
    }, [options]);

    React.useEffect(() => {
      const selectedCount = selectedValues.length;
      const allOptions = allOptionsWithDisabled;
      const totalOptions = allOptions.filter((opt) => !opt.disabled).length;
      if (selectedCount !== prevSelectedCount.current) {
        const diff = selectedCount - prevSelectedCount.current;
        if (diff > 0) {
          const addedItems = selectedValues.slice(-diff);
          const addedLabels = addedItems
            .map((value) => allOptions.find((opt) => opt.value === value)?.label)
            .filter(Boolean);

          if (addedLabels.length === 1) {
            announce(`${addedLabels[0]} selected. ${selectedCount} of ${totalOptions} options selected.`);
          } else {
            announce(`${addedLabels.length} options selected. ${selectedCount} of ${totalOptions} total selected.`);
          }
        } else if (diff < 0) {
          announce(`Option removed. ${selectedCount} of ${totalOptions} options selected.`);
        }
        prevSelectedCount.current = selectedCount;
      }

      if (isPopoverOpen !== prevIsOpen.current) {
        if (isPopoverOpen) {
          announce(`Dropdown opened. ${totalOptions} options available. Use arrow keys to navigate.`);
        } else {
          announce('Dropdown closed.');
        }
        prevIsOpen.current = isPopoverOpen;
      }

      if (searchValue !== prevSearchValue.current && searchValue !== undefined) {
        if (searchValue && isPopoverOpen) {
          const filteredCount = allOptions.filter(
            (opt) =>
              opt.label.toLowerCase().includes(searchValue.toLowerCase()) ||
              String(opt.value).toLowerCase().includes(searchValue.toLowerCase())
          ).length;

          announce(`${filteredCount} option${filteredCount === 1 ? '' : 's'} found for "${searchValue}"`);
        }
        prevSearchValue.current = searchValue;
      }
    }, [selectedValues, isPopoverOpen, searchValue, announce, allOptionsWithDisabled]);

    return (
      <>
        <div className='sr-only'>
          <div aria-live='polite' aria-atomic='true' role='status'>
            {politeMessage}
          </div>
          <div aria-live='assertive' aria-atomic='true' role='alert'>
            {assertiveMessage}
          </div>
        </div>

        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal>
          <div id={triggerDescriptionId} className='sr-only'>
            Multi-select dropdown. Use arrow keys to navigate, Enter to select, and Escape to close.
          </div>
          <PopoverTrigger asChild>
            <Button
              ref={buttonRef}
              {...props}
              // PopoverTrigger already toggles; a second onClick here cancelled it out.
              type='button'
              id={multiSelectId}
              disabled={disabled}
              variant='outline'
              // oxlint-disable-next-line jsx-a11y/role-has-required-aria-props -- aria-controls omitted on purpose; see the comment below
              role='combobox'
              aria-expanded={isPopoverOpen}
              aria-haspopup='listbox'
              // No aria-controls: cmdk assigns its own id to the list element and overrides any
              // id we pass, so pointing at one here only produces a dangling reference.
              aria-describedby={mergeAriaIds(ariaDescribedBy, triggerDescriptionId)}
              // Names the trigger from the caller's <label> plus a plain-text summary of the
              // current selection. An aria-label here would instead hide which options are
              // selected, because it overrides the badge text.
              aria-labelledby={mergeAriaIds(ariaLabelledBy, selectionSummaryId)}
              className={cn(
                'relative h-auto min-h-9 w-full min-w-70 justify-between py-0 text-left text-base font-normal md:text-sm [&_svg]:pointer-events-auto',
                autoSize ? 'w-auto' : 'w-full',
                disabled && 'cursor-not-allowed opacity-50',
                className
              )}
            >
              <span id={selectionSummaryId} className='sr-only'>
                {selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder}
              </span>
              {selectedValues.length > 0 ? (
                <div className='flex w-full items-center justify-between'>
                  <div
                    className={cn(
                      'flex items-center gap-x-1',
                      singleLine ? 'overflow-x-auto' : 'flex-wrap',
                      responsiveSettings.compactMode && 'gap-0.5'
                    )}
                    style={
                      singleLine
                        ? {
                            paddingBottom: '4px'
                          }
                        : {}
                    }
                  >
                    {selectedValues
                      .slice(0, responsiveSettings.maxCount)
                      .map((value) => {
                        const option = getOptionByValue(value);
                        const customStyle = option?.style;
                        if (!option) {
                          return null;
                        }
                        const badgeStyle: React.CSSProperties = {
                          animationDuration: `${animation}s`,
                          ...(customStyle?.badgeColor && {
                            backgroundColor: customStyle.badgeColor
                          }),
                          ...(customStyle?.gradient && {
                            background: customStyle.gradient,
                            color: 'white'
                          })
                        };
                        return (
                          <NoddiBadge
                            key={String(value)}
                            className={cn(
                              'my-1 border-foreground/10 bg-card text-foreground transition-all duration-300 ease-in-out hover:bg-card/80',
                              customStyle?.gradient && 'border-transparent text-white',
                              responsiveSettings.compactMode && 'px-1.5 py-0.5 text-xs',
                              // Responsive max widths
                              screenSize === 'mobile' && 'max-w-25',
                              screenSize === 'tablet' && 'max-w-35',
                              screenSize === 'desktop' && 'max-w-45',
                              singleLine && 'shrink-0 whitespace-nowrap',
                              '[&>svg]:pointer-events-auto'
                            )}
                            style={{
                              ...badgeStyle
                            }}
                          >
                            <span className='min-w-0 flex-1 truncate'>{option.label}</span>
                            {option.imageUrl ? (
                              <img
                                src={option.imageUrl}
                                alt={option.label}
                                className='ml-1 h-4 w-auto object-contain'
                                aria-hidden='true'
                              />
                            ) : option.icon ? (
                              <NoddiIcon name={option.icon} />
                            ) : null}
                            <div
                              role='button'
                              tabIndex={0}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleOption(value);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  toggleOption(value);
                                }
                              }}
                              aria-label={`Remove ${option.label} from selection`}
                              className='cursor-pointer rounded-xs focus:ring-1 focus:ring-white/50 focus:outline-hidden'
                            >
                              <div className='text-black hover:text-muted-foreground'>
                                <NoddiIcon name='X' size={8} className='rounded-full transition-colors' />
                              </div>
                            </div>
                          </NoddiBadge>
                        );
                      })
                      .filter(Boolean)}
                    {selectedValues.length > responsiveSettings.maxCount && (
                      <NoddiBadge
                        className={cn(
                          'my-1 border-foreground/10 bg-card text-foreground transition-all duration-300 ease-in-out hover:bg-card/80',
                          responsiveSettings.compactMode && 'px-1.5 py-0.5 text-xs',
                          singleLine && 'shrink-0 whitespace-nowrap',
                          '[&>svg]:pointer-events-auto'
                        )}
                        style={{
                          animationDuration: `${animationConfig?.duration || animation}s`,
                          animationDelay: `${animationConfig?.delay || 0}s`
                        }}
                      >
                        {`+ ${selectedValues.length - responsiveSettings.maxCount}`}
                      </NoddiBadge>
                    )}
                  </div>
                  <div className='flex items-center justify-between'>
                    <div
                      role='button'
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClear();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          handleClear();
                        }
                      }}
                      aria-label={`Clear all ${selectedValues.length} selected options`}
                      className='mx-2 flex cursor-pointer items-center justify-center rounded-xs text-muted-foreground hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring focus-visible:outline-hidden'
                    >
                      <NoddiIcon name='X' />
                    </div>
                    <NoddiSeparator orientation='vertical' className='flex h-full min-h-6' />
                    <div className='ml-2 size-4'>
                      {loading ? (
                        <NoddiSpinner size='small' />
                      ) : (
                        <NoddiIcon name='ChevronsUpDown' className='text-muted-foreground opacity-50' />
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className='mx-auto flex w-full items-center justify-between'>
                  <span className='min-w-0 flex-1 truncate text-sm text-muted-foreground' title={placeholder}>
                    {placeholder}
                  </span>
                  <div className='ml-2 size-4'>
                    {loading ? (
                      <NoddiSpinner size='small' />
                    ) : (
                      <NoddiIcon name='ChevronsUpDown' className='text-muted-foreground opacity-50' />
                    )}
                  </div>
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn('min-w-70 p-0', getPopoverAnimationClass(), popoverClassName)}
            style={{
              animationDuration: `${animationConfig?.duration || animation}s`,
              animationDelay: `${animationConfig?.delay || 0}s`,
              touchAction: 'manipulation'
            }}
            sideOffset={4}
            alignOffset={0}
            collisionPadding={screenSize === 'mobile' ? 16 : 8}
            avoidCollisions
            onEscapeKeyDown={() => setIsPopoverOpen(false)}
          >
            {/* Do NOT key the Command root; that would remount the input and steal focus. */}
            {/* Disable cmdk's internal filtering; we manage filtering via `filteredOptions` */}
            <Command
              label='Search through available options'
              className='flex h-full max-h-[60vh] flex-col'
              shouldFilter={false}
            >
              {searchable && (
                <CommandInput
                  placeholder='Search options...'
                  onKeyDown={handleInputKeyDown}
                  value={searchValue}
                  onValueChange={(val) => {
                    setSearchValue(val);
                    // Propagate up for async loading use cases
                    onSearchValueChange?.(val);
                  }}
                  aria-label='Search through available options'
                  aria-describedby={`${multiSelectId}-search-help`}
                />
              )}
              {searchable && (
                <div id={`${multiSelectId}-search-help`} className='sr-only'>
                  Type to filter options. Use arrow keys to navigate results.
                </div>
              )}

              {/* Sticky Select All at top */}
              <React.Fragment key={optionsKey}>
                {!hideSelectAll && !searchValue && enabledOptions.length > 0 && (
                  <div className='sticky top-0 z-10 border-b bg-popover p-1'>
                    <button type='button' onClick={toggleAll} className={popoverActionClassName}>
                      <div
                        className={cn(
                          'mr-2 flex size-4 items-center justify-center rounded-xs border border-primary',
                          areAllSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'
                        )}
                        aria-hidden='true'
                      >
                        <NoddiIcon name='Check' />
                      </div>
                      <span>{areAllSelected ? 'Deselect all' : `Select all (${enabledOptions.length} options)`}</span>
                    </button>
                  </div>
                )}

                {/* Scrollable options area */}
                {/* Items re-register when options change via the keyed fragment above, without affecting input focus */}
                <CommandList
                  aria-multiselectable='true'
                  className={cn('flex-1 overflow-y-auto overscroll-contain', screenSize === 'mobile' && 'max-h-[50vh]')}
                >
                  {filteredOptions.length === 0 && (
                    <>
                      {loading ? (
                        <CommandGroup className='-mb-1 h-28'>
                          {Array.from({ length: 3 }).map((_, index) => (
                            // eslint-disable-next-line noddi/enforce-spacing-scale -- not migrated yet: legacy space-x-2 skeleton row, equals gap-2 on this flex row
                            <div key={index} className='flex items-center space-x-2 rounded-xs px-2 py-1.5'>
                              <NoddiSkeleton className='size-6' />
                              <NoddiSkeleton className='h-6 flex-1' />
                            </div>
                          ))}
                        </CommandGroup>
                      ) : (
                        <div className='-mb-1 flex h-28 items-center justify-center text-center text-sm'>
                          {emptyIndicator ?? 'No options found'}
                        </div>
                      )}
                    </>
                  )}
                  {isOptionsGrouped ? (
                    (filteredOptions as MultiSelectGroup[]).map((group) => (
                      <CommandGroup key={group.heading} heading={group.heading}>
                        {group.options.map(renderOptionItem)}
                      </CommandGroup>
                    ))
                  ) : (
                    <CommandGroup>{(filteredOptions as MultiSelectOption[]).map(renderOptionItem)}</CommandGroup>
                  )}
                </CommandList>

                {/* Sticky footer with Clear and Close buttons */}
                <div className='sticky bottom-0 z-10 border-t bg-popover p-1'>
                  <div className='flex items-center justify-between'>
                    {selectedValues.length > 0 && (
                      <>
                        <button
                          type='button'
                          onClick={handleClear}
                          className={cn(popoverActionClassName, 'flex-1 justify-center')}
                        >
                          Clear
                        </button>
                        <NoddiSeparator orientation='vertical' className='flex h-full min-h-6' />
                      </>
                    )}
                    <button
                      type='button'
                      onClick={() => setIsPopoverOpen(false)}
                      className={cn(popoverActionClassName, 'max-w-full flex-1 justify-center')}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </React.Fragment>
            </Command>
          </PopoverContent>
          {animation > 0 && selectedValues.length > 0 && (
            // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- wrapper around the sparkles icon that toggles the badge animation, vendored shadcn multi-select markup
            <div onClick={() => setIsAnimating(!isAnimating)}>
              <NoddiIcon
                name='WandSparkles'
                size='xSmall'
                className={cn(
                  'my-2 cursor-pointer bg-background text-foreground',
                  isAnimating ? '' : 'text-muted-foreground'
                )}
              />
            </div>
          )}
        </Popover>
      </>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

// Real buttons rather than CommandItems: cmdk gives every item role="option", which made
// "Select all" / "Clear" / "Close" look like selectable values in the accessibility tree.
const popoverActionClassName =
  'relative flex w-full select-none items-center gap-2 rounded-xs px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground';
